import type { CustomerDetail, LineSection, ProformaStatus, ProformaVatMode, WorkType } from "@/lib/types";

// The proforma lifecycle maps onto the 8 canonical .avds-status--<state> pills —
// no new colours. SENT reuses the amber "onhold" tone ("wacht op klant").
export const STATUS_CLASS: Record<ProformaStatus, string> = {
  DRAFT: "concept",
  // The check pulses like work in progress (`review` = "in behandeling");
  // checked-and-ready takes the other in-progress tone, not a green: it has not
  // gone anywhere yet, and green is the customer's approval.
  IN_REVIEW: "review",
  REVIEWED: "inprogress",
  SENT: "onhold",
  REJECTED: "rejected",
  APPROVED: "approved",
  INVOICED: "completed",
  // Deliberately NOT the same green as INVOICED: the whole point of this state is
  // that you can see at a glance which orders went round the proforma instead of
  // through it. `submitted` is the neutral tone of the eight.
  MANUALLY_INVOICED: "submitted",
};

// Status → i18n key (status.<state>).
export const STATUS_LABEL_KEY: Record<ProformaStatus, string> = {
  DRAFT: "status.draft",
  IN_REVIEW: "status.inReview",
  REVIEWED: "status.reviewed",
  SENT: "status.sent",
  REJECTED: "status.rejected",
  APPROVED: "status.approved",
  INVOICED: "status.invoiced",
  MANUALLY_INVOICED: "status.manuallyInvoiced",
};

// Work type → i18n key.
export const WORK_TYPE_LABEL_KEY: Record<WorkType, string> = {
  INDUSTRIAL_RELOCATION: "workType.industrialRelocation",
  MACHINE_TRANSPORT: "workType.machineTransport",
  LIFTING: "workType.lifting",
  STORAGE: "workType.storage",
};

export const WORK_TYPES: WorkType[] = [
  "INDUSTRIAL_RELOCATION",
  "MACHINE_TRANSPORT",
  "LIFTING",
  "STORAGE",
];

// Tariff-line section → i18n key, in render order. This is TALIS's own order of
// tariefgroepen (user, 2026-09-15) — the proforma is a mirror of that structure,
// so a reader comparing the two documents finds the blocks in the same sequence —
// except that Expeditie leads (Van de Scheur, 2026-11-04).
// Mirrored in services/api lineOrder.ts; the two must stay identical or the PDF
// and the screen disagree about the same proforma.
export const SECTION_ORDER: LineSection[] = [
  "SHIPPING", // Expeditie
  "TRANSPORT", // Transport
  "LIFTING", // Hijswerk
  "LABOR", // Inzet (projectinzet)
  "RENTAL", // Verhuur
  "EQUIPMENT", // Hulpmateriaal (bijinzet)
  "OTHER", // Overige
];
export const SECTION_LABEL_KEY: Record<LineSection, string> = {
  LABOR: "section.labor",
  EQUIPMENT: "section.equipment",
  TRANSPORT: "section.transport",
  SHIPPING: "section.shipping",
  LIFTING: "section.lifting",
  RENTAL: "section.rental",
  OTHER: "section.other",
};

export const VAT_MODE_LABEL_KEY: Record<ProformaVatMode, string> = {
  STANDARD: "vat.standard",
  VERLEGD_08: "vat.verlegd",
  ICP_10: "vat.icp",
};

// What the customer's PDF shows. In this order in the send dialog: most detail first.
export const CUSTOMER_DETAIL_ORDER: CustomerDetail[] = ["FULL", "SECTIONS", "TOTAL"];
export const CUSTOMER_DETAIL_LABEL_KEY: Record<CustomerDetail, string> = {
  FULL: "customerDetail.full",
  SECTIONS: "customerDetail.sections",
  TOTAL: "customerDetail.total",
};
export const CUSTOMER_DETAIL_HINT_KEY: Record<CustomerDetail, string> = {
  FULL: "customerDetail.fullHint",
  SECTIONS: "customerDetail.sectionsHint",
  TOTAL: "customerDetail.totalHint",
};
